/**
 * EnabledFeaturesContext — controls which of F1..F4 are visible.
 *
 * The harness (Sprint 3) flips features on as the participant moves
 * through Phase 0 (none) → Phase 1 (F1, then F3, then F4) → Phase 2
 * (F2 with D2/D4 variants). Within Sprint 1 the App defaults to
 * everything-on so the demo works unchanged.
 *
 * @typedef {'F1' | 'F2' | 'F3' | 'F4'} FeatureId
 */
import { createContext, useContext } from 'react';

export const ALL_FEATURES = ['F1', 'F2', 'F3', 'F4'];

// Two different defaults on purpose: the createContext default is
// ALL_FEATURES so un-Provided consumers (isolated unit tests) work,
// while the Provider falls back to [] so a participant build with no
// `value` prop renders the clean baseline rather than every feature.
const EnabledFeaturesContext = createContext(ALL_FEATURES);

export function EnabledFeaturesProvider({ value, children }) {
  return (
    <EnabledFeaturesContext.Provider value={value || []}>
      {children}
    </EnabledFeaturesContext.Provider>
  );
}

export function useEnabledFeatures() {
  return useContext(EnabledFeaturesContext);
}

/**
 * @param {FeatureId} feature
 * @returns {boolean}
 */
export function useIsFeatureEnabled(feature) {
  const enabled = useEnabledFeatures();
  return enabled.includes(feature);
}

/**
 * Map a URL pathname to an enabledFeatures array.
 *
 * Invariant the chat window enforces: at most one of
 * {F1 Mirror button, F3 RiskBar, F4 hotspot tints} is visible at
 * any moment — they must not coexist. Routes are the selector:
 *
 *   /         → no features  (clean baseline, study Phase 0)
 *   /mirror   → F1 only
 *   /risk     → F3 only
 *   /hotspot  → F4 only
 *   /whatif   → F2 only
 *   /banner   → banner view via resolveResearcherView()
 *
 * Anything unrecognized falls back to the clean baseline so a typo or
 * external deep-link doesn't accidentally render multiple features at
 * once. Pure function — tested in enabled-features.test.jsx.
 *
 * @param {string} pathname
 * @returns {[] | [FeatureId]}  empty for clean baseline, or exactly one FeatureId
 */
export function resolveFeaturesFromPath(pathname) {
  const p = (pathname || '/').replace(/\/+$/, '') || '/';
  if (p === '/mirror') return ['F1'];
  if (p === '/risk') return ['F3'];
  if (p === '/hotspot') return ['F4'];
  if (p === '/whatif') return ['F2'];
  return [];
}

export function resolveResearcherView(pathname) {
  const p = (pathname || '/').replace(/\/+$/, '') || '/';
  return {
    features: resolveFeaturesFromPath(p),
    banner: p === '/banner',
  };
}
